import { observer } from "mobx-react-lite"
import styles from "../styles/joinTripConfirmation.module.scss"
import { useMutation, useQuery } from "@tanstack/react-query"
import { exchangeAuthToken } from "../services/firebase-functions/auth.ts"
import { makeAutoObservable } from "mobx"
import { useMemo } from "react"
import { ModalWrapper } from "../components/templates/ModalWrapper/index.tsx"
import { Checkbox } from "../components/atoms/Checkbox/index.tsx"
import CloseModalButton from "../components/buttons/CloseModalButton/index.tsx"
import { LoadingScreen } from "../components/templates/LoadingScreen/index.tsx"
import { UserID } from "@cruisen/contract"
import { UserModel, formatPhoneNumber } from "@cruisen/react-shared"
import { useStores } from "../models/helpers/useStores.ts"
import { firebaseAuthUsingToken } from "../services/auth.ts"
import { fetchUserByUid } from "../services/firestore/user.ts"
import { useParams } from "react-router-dom"
import { StatusScreen } from "../components/templates/StatusScreen/index.tsx"
import { getAgentDetailsByCode } from "../services/firebase-functions/agentProfile.ts"
import AdvisorImageBorder from "../assets/advisorImageBorder.png"
import UserAvatar from "../components/atoms/UserAvatar/index.tsx"
import { Helmet } from "react-helmet-async"
import classNames from "classnames"
import { joinTrip } from "../services/firebase-functions/trips.ts"
import AlertTriangleOutline from "~icons/eva/alert-triangle-outline"
import { getTrip } from "../services/firestore/trips.ts"

class JoinTripConfirmationStore {
  // next query params are optional we will get them from auth token meta
  agentCode: string | null
  tripId: string | null
  tripName: string | null

  termsAndConditionsAccepted: boolean
  termsAndConditionsModalOpen: boolean
  userResponse: "pending" | "accepted" | "declined"

  constructor(routeParams: Record<string, string> = {}) {
    const searchParams = new URLSearchParams(window.location.search)
    this.agentCode = searchParams.get("agentCode")
    this.tripId = routeParams.tripId || searchParams.get("tripId")
    this.tripName = searchParams.get("tripName")

    this.termsAndConditionsAccepted = false
    this.termsAndConditionsModalOpen = false
    this.userResponse = "pending"

    makeAutoObservable(this)
  }

  setFromAuthMeta(meta?: Record<string, unknown>) {
    // populate context identifiers from auth meta
    if (!this.agentCode) this.agentCode = meta?.agentCode?.toString()
    if (!this.tripId) this.tripId = meta?.tripId?.toString()
    if (!this.tripName) this.tripName = meta?.tripName?.toString()
  }

  setTermsAndConditionsAccepted(accepted: boolean) {
    this.termsAndConditionsAccepted = accepted
  }

  setTermsAndConditionsModalOpen(open: boolean) {
    this.termsAndConditionsModalOpen = open
  }

  setUserResponse(userResponse: "pending" | "accepted" | "declined") {
    this.userResponse = userResponse
  }
}

export const JoinTripConfirmation = observer(function JoinTripConfirmation() {
  const routeParams = useParams()
  const { token } = routeParams

  const rootStore = useStores()
  const store = useMemo(() => new JoinTripConfirmationStore(routeParams), [token])

  const { data: loggedInUserId, isLoading: isAuthLoading } = useQuery({
    queryKey: ["exchangeAuthToken", token],
    queryFn: async (): Promise<UserID | null> => {
      // already authenticated and has all query params
      if (rootStore.loggedInUser && store.tripId && store.agentCode)
        return rootStore.loggedInUser.id

      // token in empty in url
      if (!token) return null

      const { authToken, meta } = (await exchangeAuthToken(token)) ?? {}

      const authedUser = await firebaseAuthUsingToken(authToken)
      const user = await fetchUserByUid(authedUser?.user?.uid || "")
      // user not found
      if (!user) return null

      store.setFromAuthMeta(meta)

      const loggedInUser = UserModel.create(user)
      rootStore.setProp("loggedInUser", loggedInUser)

      return loggedInUser.id
    },
  })

  const { data: trip, isLoading: isTripLoading } = useQuery({
    queryKey: ["trip", store.tripId],
    queryFn: () => getTrip(store.tripId),
    staleTime: 0,
    enabled: !!loggedInUserId && !!store.tripId,
  })

  const hasJoinedTrip =
    trip && loggedInUserId ? trip.collaboratorIds.includes(loggedInUserId) : false

  const { data: agentDetails, isLoading: isAgentDetailsLoading } = useQuery({
    queryKey: ["agentDetails", store.agentCode],
    queryFn: () => getAgentDetailsByCode(store.agentCode),
    staleTime: 0,
    enabled: !!loggedInUserId && !!store.agentCode,
  })

  const isNewAdvisor =
    rootStore.loggedInUser?.agentCode && rootStore.loggedInUser?.agentCode !== store.agentCode

  const generateErrorDetails = (): { title: string | null; description: string | null } => {
    if (!token) {
      return {
        title: "Invalid url",
        description: "Please check the url and try again",
      }
    } else if (!isAuthLoading && !rootStore.loggedInUser?.id) {
      return {
        title: "User not found",
        description: "Please check the url and try again",
      }
    } else if (!agentDetails && !isAgentDetailsLoading) {
      return {
        title: "Advisor not found",
        description: "Please check the url and try again",
      }
    } else if (store.userResponse === "declined") {
      return {
        title: "Trip invitation declined",
        description: "You will no longer receive text messages from your advisor.",
      }
    } else {
      return {
        title: null,
        description: null,
      }
    }
  }

  const { mutate: mutateJoinTrip, isPending: isJoiningTrip } = useMutation({
    mutationFn: () =>
      joinTrip({
        tripId: store.tripId,
        joinRoom: false,
        notification: false,
      }),
    onSuccess: () => {
      store.setUserResponse("accepted")
    },
  })

  const { title, description } = generateErrorDetails()

  const isLoading = isAuthLoading || isAgentDetailsLoading || isTripLoading

  if (isLoading) {
    return <LoadingScreen />
  }

  if (title && description) {
    return <StatusScreen title={title} description={description} error={true} />
  }

  if (store.userResponse === "accepted") {
    return (
      <StatusScreen
        title="Welcome aboard!"
        description="You are now a member of the trip, you can start chatting through text messages."
        error={false}
      />
    )
  }

  return (
    <div className={styles.pageContainer} data-modal-visible={"false"}>
      <Helmet>
        <title>Join trip</title>
        <meta name="viewport" content="width=device-width, maximum-scale=1.0" />
      </Helmet>
      <h1 className={styles.title}>Your advisor</h1>
      <div className={styles.agentAvatar}>
        <img src={AdvisorImageBorder} alt="Advisor" />
        <div className={styles.agentAvatarInner}>
          <UserAvatar
            size="3xl"
            avatar={agentDetails?.agentUser.avatar}
            isPrimary={true}
            nameInitals={`${agentDetails?.agentUser.firstName[0]}${agentDetails?.agentUser.lastName[0]}`}
            fontSize="30px"
          />
        </div>
        <div className={classNames(styles.gradient, styles.topGradient)} />
        <div className={classNames(styles.gradient, styles.bottomGradient)} />
      </div>
      <div className={styles.agentNameContainer}>
        <p className={styles.agentName}>
          {agentDetails?.agentUser.firstName} {agentDetails?.agentUser.lastName}
        </p>
        <p className={styles.agentDescription}>
          {!hasJoinedTrip
            ? `Your advisor invited you on a trip${store.tripName ? ` "${store.tripName}"` : ""}, accept the invitation to start chatting.`
            : `You have successfully joined the trip${store.tripName ? ` "${store.tripName}"` : ""}, you can now start chatting through text messages.`}
        </p>
      </div>
      <div className={styles.divider} />
      <div className={styles.contentContainer}>
        {isNewAdvisor && !hasJoinedTrip && (
          <div className={styles.newAdvisor}>
            <div className={styles.heading}>
              <AlertTriangleOutline className={styles.icon} />
              <p className={styles.headingText}>New advisor</p>
            </div>
            <p className={styles.newAdvisorText}>
              <b>{`${agentDetails?.agentUser.firstName} ${agentDetails?.agentUser.lastName}`}</b>{" "}
              will replace your current advisor and become your dedicated travel advisor for this
              and all future trips.
            </p>
          </div>
        )}
        <div className={styles.agentInfoContainer}>
          <div className={styles.agentInfo}>
            <div className={styles.agentInfoItem}>
              <p className={styles.label}>Email</p>
              <p className={styles.value}>{agentDetails?.agentUser.email}</p>
            </div>
            <div className={styles.agentInfoItem}>
              <p className={styles.label}>Phone number</p>
              <p className={styles.value}>
                {formatPhoneNumber(agentDetails?.agentUser.phoneNumber)}
              </p>
            </div>
            <div className={styles.agentInfoItem}>
              <p className={styles.label}>Best travel advice</p>
              <p className={styles.value}>{agentDetails?.agentProfile.bestTravelAdvice}</p>
            </div>
          </div>
        </div>
        {agentDetails?.agentProfile?.customTerms && !hasJoinedTrip && (
          <div className={styles.termsContainer}>
            <Checkbox
              isChecked={store.termsAndConditionsAccepted}
              onChange={(accepted) => {
                if (store.termsAndConditionsModalOpen) return
                store.setTermsAndConditionsAccepted(accepted)
              }}
            >
              <p className={styles.termsText}>
                I have read and agree to the advisor's{" "}
                <span
                  className={styles.termsTextButton}
                  onClick={() => store.setTermsAndConditionsModalOpen(true)}
                >
                  Terms and Conditions
                </span>
              </p>
            </Checkbox>
          </div>
        )}
        {!hasJoinedTrip && (
          <div className={styles.buttonContainer}>
            <button
              className={styles.button}
              disabled={
                (agentDetails?.agentProfile?.customTerms && !store.termsAndConditionsAccepted) ||
                isJoiningTrip ||
                !store.tripId
              }
              onClick={() => mutateJoinTrip()}
            >
              {isJoiningTrip ? "Joining..." : "Accept"}
            </button>
            <button
              onClick={() => store.setUserResponse("declined")}
              className={styles.cancelButton}
            >
              Decline
            </button>
          </div>
        )}
      </div>
      {store.termsAndConditionsModalOpen && (
        <ModalWrapper>
          <div className={styles.termsAndConditionsModal}>
            <div className={styles.termsAndConditionsModalHeading}>
              <h2 className={styles.termsAndConditionsModalTitle}>Terms and Conditions</h2>
              <CloseModalButton onClick={() => store.setTermsAndConditionsModalOpen(false)} />
            </div>
            <p
              className={styles.termsAndConditionsModalText}
              dangerouslySetInnerHTML={{ __html: agentDetails?.agentProfile?.customTerms }}
            />
          </div>
        </ModalWrapper>
      )}
    </div>
  )
})
